/**
 * Vendors and what the farm owes them (BR-10, P1-BR5).
 *
 * The payables total is one of the few figures that tells the partners their
 * real position rather than their cash position — ৳50,000 in the box means
 * something different when the feed store is owed ৳30,000 of it.
 *
 * A negative payable is shown rather than clamped to zero: it means the farm
 * has overpaid, and showing it is how a double payment gets noticed.
 *
 * The list was read-only until now, which made P1-BR5 unreachable: a credit
 * purchase has to name the vendor it is owed to, and there was no way to create
 * one. The feed shop a partner actually buys from could not exist in the app.
 */

import { useState } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { errorFields, errorMessageKey } from '../../lib/apiError.js';
import { formatMoney, isNegativeAmount } from '../../lib/currency.js';
import { useAuth } from '../auth/AuthContext.js';
import { useCreateVendor, useUpdateVendor, useVendors, type Vendor } from './api.js';

/**
 * Adding or editing a shop or supplier.
 *
 * Only the name is required. The dalal at a cattle haat may have no number
 * worth keeping, and refusing to record him until one is found is how a
 * purchase ends up filed against nobody.
 */
function VendorForm({ existing, onDone }: { existing?: Vendor; onDone: () => void }): JSX.Element {
  const { t } = useTranslation();
  const create = useCreateVendor();
  const update = useUpdateVendor();

  const [name, setName] = useState(existing?.name ?? '');
  const [phone, setPhone] = useState(existing?.phone ?? '');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const pending = create.isPending || update.isPending;
  const canSubmit = name.trim().length >= 2 && !pending;

  async function submit(): Promise<void> {
    setErrorKey(null);
    setFieldErrors({});

    try {
      if (existing) {
        await update.mutateAsync({
          id: existing.id,
          // An emptied box clears the number rather than leaving the old one.
          changes: { name: name.trim(), phone: phone.trim() === '' ? null : phone.trim() },
        });
      } else {
        await create.mutateAsync({
          name: name.trim(),
          ...(phone.trim() === '' ? {} : { phone: phone.trim() }),
        });
      }
      onDone();
    } catch (error) {
      setErrorKey(errorMessageKey(error));
      setFieldErrors(errorFields(error) ?? {});
    }
  }

  return (
    <form
      className={existing ? 'mt-3 border-t border-gray-100 pt-3' : 'mb-4 rounded-xl bg-white p-4 shadow-sm'}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      noValidate
    >
      {!existing && <h2 className="mb-3 text-base font-semibold text-gray-800">{t('finance.vendors.addTitle')}</h2>}

      <label
        className="mb-1 block text-sm font-medium text-gray-700"
        htmlFor={`vendor-name-${String(existing?.id ?? 'new')}`}
      >
        {t('finance.vendors.name')}
      </label>
      <input
        id={`vendor-name-${String(existing?.id ?? 'new')}`}
        value={name}
        onChange={(event) => setName(event.target.value)}
        maxLength={120}
        className="mb-3 min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3"
      />
      {fieldErrors['name'] !== undefined && <p className="-mt-2 mb-2 text-sm text-red-700">{fieldErrors['name']}</p>}

      <label
        className="mb-1 block text-sm font-medium text-gray-700"
        htmlFor={`vendor-phone-${String(existing?.id ?? 'new')}`}
      >
        {t('finance.vendors.phone')}
      </label>
      <input
        id={`vendor-phone-${String(existing?.id ?? 'new')}`}
        type="tel"
        inputMode="numeric"
        value={phone}
        onChange={(event) => setPhone(event.target.value)}
        placeholder="01XXXXXXXXX"
        className="mb-3 min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3"
      />
      {fieldErrors['phone'] !== undefined && <p className="-mt-2 mb-2 text-sm text-red-700">{fieldErrors['phone']}</p>}

      {errorKey !== null && (
        <div role="alert" className="mb-3 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          <p>{t(errorKey)}</p>
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!canSubmit}
          className="min-h-[52px] flex-1 rounded-xl bg-green-700 text-base font-medium text-white disabled:opacity-50"
        >
          {t(pending ? 'finance.vendors.saving' : 'finance.vendors.save')}
        </button>
        {existing && (
          <button
            type="button"
            onClick={onDone}
            className="min-h-touch rounded-xl bg-gray-100 px-4 py-2 text-sm text-gray-700"
          >
            {t('app.cancel')}
          </button>
        )}
      </div>

      {/* Deactivation, never deletion (BR-08): the payable owed to this shop
          and every purchase filed against it stay attributable. */}
      {existing && (
        <button
          type="button"
          onClick={() => void update.mutateAsync({ id: existing.id, changes: { isActive: !existing.isActive } })}
          disabled={pending}
          className={`mt-2 min-h-touch w-full rounded-xl px-4 py-2 text-sm disabled:opacity-50 ${
            existing.isActive ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'
          }`}
        >
          {t(existing.isActive ? 'finance.vendors.deactivate' : 'finance.vendors.reactivate')}
        </button>
      )}
    </form>
  );
}

export function VendorsScreen(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: vendors = [], isLoading, isError, refetch } = useVendors();
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Suppliers are who the farm owes money to, which §1.4 makes a partner's job.
  const canManage = user?.role === 'admin' || user?.role === 'partner';

  const owed = vendors.filter((vendor) => vendor.payable !== '0.00');

  return (
    <div className="page-container">
      <h1 className="py-4 text-xl font-semibold text-gray-800">{t('finance.vendors.title')}</h1>

      {canManage &&
        (isAdding ? (
          <VendorForm onDone={() => setIsAdding(false)} />
        ) : (
          <button
            type="button"
            onClick={() => setIsAdding(true)}
            className="mb-4 flex min-h-touch w-full items-center justify-between rounded-xl bg-green-700 px-4 py-3 text-white"
          >
            <span className="text-sm font-medium">{t('finance.vendors.add')}</span>
            <span aria-hidden="true">+</span>
          </button>
        ))}

      {isLoading && (
        <p role="status" className="py-8 text-center text-gray-500">
          {t('app.loading')}
        </p>
      )}

      {isError && (
        <div role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          <p className="mb-2">{t('error.network')}</p>
          <button type="button" onClick={() => void refetch()} className="min-h-touch font-medium underline">
            {t('app.retry')}
          </button>
        </div>
      )}

      {vendors.length === 0 && !isLoading && (
        <p className="py-8 text-center text-gray-500">{t('finance.vendors.empty')}</p>
      )}

      {owed.length > 0 && (
        <p className="mb-4 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {t('finance.vendors.owedCount', { count: owed.length })}
        </p>
      )}

      <ul className="space-y-2">
        {vendors.map((vendor) => (
          <li
            key={vendor.id}
            className={`rounded-xl bg-white px-4 py-3 shadow-sm ${vendor.isActive ? '' : 'opacity-70'}`}
          >
            <div className="flex items-baseline justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate font-medium text-gray-800">
                  {vendor.name}
                  {!vendor.isActive && (
                    <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-normal text-gray-600">
                      {t('finance.vendors.inactive')}
                    </span>
                  )}
                </p>
                {vendor.phone !== null && <p className="text-xs text-gray-500">{vendor.phone}</p>}
              </div>
              <span
                className={`shrink-0 tabular-nums font-semibold ${
                  // Owed money is the thing to notice; overpaid is unusual enough
                  // to deserve its own colour.
                  isNegativeAmount(vendor.payable)
                    ? 'text-blue-700'
                    : vendor.payable === '0.00'
                      ? 'text-gray-400'
                      : 'text-amber-800'
                }`}
              >
                {formatMoney(vendor.payable)}
              </span>
            </div>

            {canManage && editingId !== vendor.id && (
              <button
                type="button"
                onClick={() => setEditingId(vendor.id)}
                className="mt-2 min-h-touch text-sm font-medium text-green-800 underline"
              >
                {t('finance.vendors.edit')}
              </button>
            )}

            {canManage && editingId === vendor.id && <VendorForm existing={vendor} onDone={() => setEditingId(null)} />}
          </li>
        ))}
      </ul>
    </div>
  );
}
